/** @format */

import React, { type FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getPhaseOfDateRange } from "senf-shared";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import { useDiscardModal } from "../../../hooks/useDiscardModal";
import Transition from "../../atoms/animations/Transition";
import Box from "../../atoms/box/Box";
import Geocoder from "../../atoms/geocoder/Geocoder";
import Button from "../../core/button/Button";
import Seperator from "../../core/seperator/Seperator";
import Tabs from "../../molecules/tabs/Tabs";
import ParticipationModule from "../../special/modalContents/ParticipationModule";
import MainTemplatesLayout from "../../templates/layout/MainTemplatesLayout";
import PostIdeaForm from "./PostIdeaForm";
import PostIdeaButton from "./PostIdeaButton";

interface PostIdeaProps {
  ideaMap: any;
  addIdeaOptions: any;
  addIdeaOpen: boolean;
  formik: any;
  statefulMap: any;
  editIdea: boolean;
  isSubmitting: boolean;
  handleSubmit: any;
  loading: boolean;
  isOut: boolean;
  handle: any;
  project: any;
  index: number;
  setIndex: any;
  hide: boolean;
  geoDataType: string;
  setGeoDataType: any;
  bbox: any;
  variant: "mainApp" | "dashboard" | null;
} 

const PostIdea: FC<PostIdeaProps> = ({
  ideaMap,
  addIdeaOptions,
  addIdeaOpen,
  formik,
  statefulMap,
  editIdea,
  isSubmitting,
  handleSubmit,
  loading,
  isOut,
  handle,
  project,
  index,
  setIndex,
  hide,
  geoDataType,
  setGeoDataType,
  bbox,
  variant,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { displayDiscardModal } = useDiscardModal();

  const [open, setOpen] = useState<string | boolean | null>("bottom");
  const innerHeight = useRef(window.innerHeight);
  useEffect(() => {
    if (index === 1) {
      setOpen("top");
    }
  }, [index]);

  const btnConditions = [
    {
      condition: !formik?.values?.address && geoDataType === "location",
      notice: t("dialogMap:add.locationRequired"),
    },
    {
      condition: isOut,
      notice: t("dialogMap:add.outOfProjectArea"),
    },
  ];

  const resultCondition = btnConditions.find((item) => item.condition);
  const disabled = !!resultCondition;
  const disabledNotice = resultCondition
    ? resultCondition.notice
    : t("dialogMap:add.unknownError");
  return (
    <React.Fragment>
      {isMobile && addIdeaOpen && !hide && geoDataType === "location" && (
        <Box
          position="absolute"
          top="0px"
          margin="10px"
          zIndex={99}
          width="calc(100% - 20px)"
        >
          <Geocoder
            formik={formik}
            statefulMap={statefulMap}
            variant="white"
            bbox={bbox}
            value={formik.values.address}
          />
        </Box>
      )}
      <MainTemplatesLayout
        header={
          editIdea
            ? t("util:edit", { val: t("global:post") })
            : t("util:compose", { val: t("global:post") })
        }
        handleButtonClose={() =>
          displayDiscardModal({
            title: t("dialogMap:add.discardTitle"),
            description: t("dialogMap:add.discardDescription"),
            submitText: t("dialogMap:add.discardConfirmButton"),
            onSubmit: async () => {
              formik?.resetForm();
              handle.openAddIdea(false);
            },
          })
        }
        options={{
          openTo: "bottom", // bottom only works if defined in options
          open,
          // onChange: setPosition,
          positions: {
            top: innerHeight.current - 78,
            middle: 0,
            bottom:
              ideaMap?.addIdeaOptions &&
              Object?.keys(ideaMap?.addIdeaOptions)?.length
                ? 210
                : 210,
          },
        }}
        closeButtonTooltipLabel={t("dialogMap:add.discardTitle")}
      >
        <div className="h- px-5">
          <Box
            display="flex"
            gap="16px"
            flexDirection="column"
            width="100%"
            height="auto"
            marginBottom="16px"
          >
            {(ideaMap?.addIdeaOptions?.generalIdeas ||
              ideaMap?.addIdeaOptions?.drawPath ||
              ideaMap?.addIdeaOptions?.drawArea) && (
              <Tabs
                type="filled"
                classNames={{
                  wrapper: "h-11 max-w-full",
                }}
                hideTabText={
                  ideaMap?.addIdeaOptions?.drawPath ||
                  ideaMap?.addIdeaOptions?.drawArea
                }
                active={geoDataType}
                setActive={setGeoDataType}
                tabs={addIdeaOptions}
              />
            )}
            {!isMobile && geoDataType === "location" && (
              <Geocoder
                formik={formik}
                statefulMap={statefulMap}
                bbox={bbox}
                value={formik.values.address}
              />
            )}
          </Box>
          <Transition
            show={index === 0}
            from={{ x: 0, y: 200, scale: 1.1 }}
            leave={{ x: -200, scale: 1 }}
            style={{
              width: "calc(100% - 40px)",
            }}
          >
            {!ideaMap?.interactionOptions?.allowPosts && variant === "mainApp" ? <Button
            disabled={true}
            size="xl"
            text={t("dialogMap:add.disabled")}
            className="w-full"
            />
            : (!ideaMap?.interactionOptions?.allowPosts && variant !== "mainApp") || getPhaseOfDateRange(ideaMap?.startDate, ideaMap?.endDate) ===
            "while" ? (
              <Button
                trackId="addIdeaPage_continueClick"
                variant="primary"
                width="max"
                size="xl"
                text={t("global:next")}
                onClick={() => setIndex(1)}
                // state={disabled ? "disabled" : "default"} // REMOVE THIS LATER ON
                disabled={disabled}
                disabledNotice={disabledNotice}
              />
            ) : (
              <PostIdeaButton ideaMap={ideaMap} handle={handle} />

            )}
          </Transition>
          <Transition
            show={index === 1}
            from={{ x: 0, y: 100, scale: 1 }}
            leave={{ x: -200, scale: 1 }}
            style={{
              width: "100%",
              position: "relative",
            }}
          >
            <div className="-mx-5 my-5">
              <Seperator orientation="horizontal" />
            </div>

            <PostIdeaForm
              ideaMap={ideaMap}
              formik={formik}
              isSubmitting={isSubmitting}
              handleSubmit={handleSubmit}
              editIdea={editIdea}
              loading={loading}
              isOut={isOut}
              project={project}
              geoDataType={geoDataType}
            />
          </Transition>
        </div>
      </MainTemplatesLayout>
    </React.Fragment>
  );
};

export default PostIdea;
